import { reactive } from 'vue'
import { getGlobalVariables, getWorkflowVariables, type Variable } from '@/store/variables.store'
import { getResources, type ResourceType } from '@/store/resource.store'
import { getDefaultFileSystem } from '@components/flow/services/enhanced/FileSystemContext'

// 依赖类型定义
export interface Dependency {
  id: string
  name: string
  version: string
  type: 'umd' | 'cjs' | 'es'
  url?: string
  source: 'cdn' | 'custom'
  description?: string
  entryPoint?: string
  globals?: Record<string, string>
  isLoaded?: boolean
  loadError?: string
}

// 依赖管理存储
const state = reactive({
  dependencies: [
    { id: '1', name: 'lodash', version: '4.17.21', type: 'umd', source: 'cdn', url: 'https://cdn.jsdelivr.net/npm/lodash@4.17.21/lodash.min.js', globals: { '_': 'lodash' }, isLoaded: false },
    { id: '2', name: 'axios', version: '0.27.2', type: 'es', source: 'cdn', url: 'https://cdn.jsdelivr.net/npm/axios@0.27.2/dist/axios.min.js', isLoaded: false },
    { id: '3', name: 'dayjs', version: '1.11.7', type: 'cjs', source: 'cdn', url: 'https://cdn.jsdelivr.net/npm/dayjs@1.11.7/dayjs.min.js', isLoaded: false }
  ] as Dependency[],
  loadedDependencies: new Map<string, any>()
})

// 动态加载脚本
async function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

// 动态加载ES模块
async function loadModule(url: string): Promise<any> {
  try {
    return await import(/* @vite-ignore */ url)
  } catch (error) {
    throw new Error(`Failed to load module: ${url}`)
  }
}

// 添加依赖
export function addDependency(dependency: Omit<Dependency, 'id'>): void {
  const newDep: Dependency = {
    ...dependency,
    id: Date.now().toString()
  }
  state.dependencies.push(newDep)
  saveDependencies()
}

// 删除依赖
export function deleteDependency(id: string): void {
  const index = state.dependencies.findIndex(dep => dep.id === id)
  if (index >= 0) {
    state.dependencies.splice(index, 1)
    state.loadedDependencies.delete(id)
    saveDependencies()
  }
}

// 获取依赖列表
export function getDependencies(type?: 'umd' | 'cjs' | 'es'): Dependency[] {
  return type 
    ? state.dependencies.filter(dep => dep.type === type)
    : state.dependencies
}

// 加载依赖
export async function loadDependency(id: string): Promise<void> {
  const dependency = state.dependencies.find(dep => dep.id === id)
  if (!dependency) {
    throw new Error(`Dependency not found: ${id}`)
  }

  if (dependency.isLoaded) {
    return // 已经加载过了
  }

  try {
    let loadedModule: any = null

    if (dependency.type === 'umd') {
      await loadScript(dependency.url!)
      // UMD 通常暴露全局变量
      if (dependency.globals) {
        for (const [global] of Object.entries(dependency.globals)) {
          loadedModule = (window as any)[global]
          break
        }
      }
    } else if (dependency.type === 'es') {
      loadedModule = await loadModule(dependency.url!)
    } else if (dependency.type === 'cjs') {
      // CJS 模块可能需要特殊处理
      loadedModule = await loadModule(dependency.url!)
    }

    dependency.isLoaded = true
    dependency.loadError = undefined
    state.loadedDependencies.set(id, loadedModule)
    saveDependencies()
  } catch (error) {
    dependency.loadError = error instanceof Error ? error.message : 'Unknown error'
    throw error
  }
}

// 批量预加载指定 ids
export async function preloadDependenciesByIds(ids: string[]): Promise<void> {
  const tasks = ids.map(id => loadDependency(id).catch(() => undefined))
  await Promise.all(tasks)
}

// 获取已加载的依赖
export function getLoadedDependency(id: string): any {
  return state.loadedDependencies.get(id)
}

// 创建自定义函数的全局上下文
export function createFunctionContext(): Record<string, any> {
  const context: Record<string, any> = {
    $fs: getDefaultFileSystem(),
  }
  
  // 注入依赖模块
  for (const [id, module] of state.loadedDependencies) {
    const dependency = state.dependencies.find(dep => dep.id === id)
    if (dependency && dependency.isLoaded) {
      context[dependency.name] = module
    }
  }

  // 注入只读变量访问器
  const toMap = (vars: Variable[]) => vars.reduce((acc, v) => { acc[v.key] = v.value; return acc }, {} as Record<string, any>)
  const variablesAccessor = {
    get global() { return toMap(getGlobalVariables()) },
    get workflow() { return toMap(getWorkflowVariables()) },
    get(key: string): any {
      const w = getWorkflowVariables().find(v => v.key === key)
      if (w) return w.value
      const g = getGlobalVariables().find(v => v.key === key)
      return g?.value
    },
    keys(scope: 'global' | 'workflow' | 'all' = 'all'): string[] {
      if (scope === 'global') return getGlobalVariables().map(v => v.key)
      if (scope === 'workflow') return getWorkflowVariables().map(v => v.key)
      const set = new Set<string>([...getGlobalVariables().map(v => v.key), ...getWorkflowVariables().map(v => v.key)])
      return Array.from(set)
    },
    entries(scope: 'global' | 'workflow' | 'all' = 'all'): Array<{ key: string; value: any }> {
      const pick = (arr: Variable[]) => arr.map(v => ({ key: v.key, value: v.value }))
      if (scope === 'global') return pick(getGlobalVariables())
      if (scope === 'workflow') return pick(getWorkflowVariables())
      return [...pick(getGlobalVariables()), ...pick(getWorkflowVariables())]
    }
  }
  Object.defineProperty(context, '$variables', { value: variablesAccessor, enumerable: true, configurable: false, writable: false })

  // 注入资源访问器
  const resourcesAccessor = {
    list(filter: ResourceType | 'all' = 'all') {
      return getResources(filter).slice()
    },
    getById(id: string) {
      return getResources('all').find(r => r.id === id)
    },
    getByName(name: string) {
      return getResources('all').find(r => r.name === name)
    },
    url(id: string) {
      const r = getResources('all').find(x => x.id === id)
      return r?.url
    }
  }
  Object.defineProperty(context, '$resources', { value: resourcesAccessor, enumerable: true, configurable: false, writable: false })
  
  return context
}

// 持久化依赖配置
function saveDependencies(): void {
  try {
    const data = state.dependencies.map(dep => ({
      ...dep,
      isLoaded: false // 重新启动时需要重新加载
    }))
    localStorage.setItem('mflow_dependencies', JSON.stringify(data))
  } catch (error) {
    console.error('Failed to save dependencies:', error)
  }
}

// 从本地存储加载依赖配置
export function loadDependencies(): void {
  try {
    const saved = localStorage.getItem('mflow_dependencies')
    if (saved) {
      const data = JSON.parse(saved)
      state.dependencies.splice(0, state.dependencies.length, ...data)
    }
  } catch (error) {
    console.error('Failed to load dependencies:', error)
  }
}

// 新增：依赖导出/导入能力（不包含 isLoaded/loadError，恢复后需重新加载）
export interface DependenciesSnapshot {
  dependencies: Omit<Dependency, 'isLoaded' | 'loadError'>[]
}

export function exportDependencies(): DependenciesSnapshot {
  return {
    dependencies: state.dependencies.map(({ isLoaded, loadError, ...rest }) => ({ ...rest }))
  }
}

export function importDependencies(snapshot: DependenciesSnapshot): void {
  const list = (snapshot?.dependencies ?? []).map(d => ({ ...d, isLoaded: false as const }))
  state.dependencies.splice(0, state.dependencies.length, ...list)
  state.loadedDependencies.clear()
  saveDependencies()
}

// 初始化
loadDependencies()

export { state as dependencyState }